import { useEffect, useRef, useState } from "react";
import {
  discardApkUpload,
  pickStoredApk,
  uploadApk,
} from "@utils/api-communication/HeadsetReleasesCli";
import type { ApkInspection, HardwareFamily, StoredApk } from "types/headsetReleaseTypes";
import { releaseErrorMessage } from "./notifyError";

export interface FamilyUploadState {
  fileName: string;
  sizeBytes: number;
  fromStorage: boolean;
  cancel: AbortController;
  progress: number;
  uploadId?: string;
  inspection?: ApkInspection;
  error?: string;
  saved?: boolean;
}

export type FamilyUploads = Partial<Record<HardwareFamily, FamilyUploadState>>;

type UploadedHandler = (
  family: HardwareFamily,
  uploadId: string,
  inspection: ApkInspection,
  fromStorage: boolean,
) => void;

function abandonUpload(upload: FamilyUploadState) {
  if (upload.saved) return;
  if (upload.uploadId) discardApkUpload(upload.uploadId).catch(() => undefined);
  else upload.cancel.abort();
}

export function useFamilyUploads(onUploaded?: UploadedHandler) {
  const [uploads, setUploads] = useState<FamilyUploads>({});
  const uploadsOnPage = useRef(uploads);
  uploadsOnPage.current = uploads;
  const latestOnUploaded = useRef(onUploaded);
  latestOnUploaded.current = onUploaded;

  useEffect(() => () => Object.values(uploadsOnPage.current).forEach(abandonUpload), []);

  const patch = (
    family: HardwareFamily,
    cancel: AbortController,
    change: Partial<FamilyUploadState>,
  ) =>
    setUploads((current) => {
      const upload = current[family];
      return upload?.cancel === cancel
        ? { ...current, [family]: { ...upload, ...change } }
        : current;
    });

  const remove = (family: HardwareFamily) => {
    const upload = uploadsOnPage.current[family];
    if (upload) abandonUpload(upload);
    setUploads(({ [family]: _removed, ...rest }) => rest);
  };

  const track = (
    family: HardwareFamily,
    source: Pick<FamilyUploadState, "fileName" | "sizeBytes" | "fromStorage">,
    send: (cancel: AbortController) => Promise<{ uploadId: string; inspection: ApkInspection }>,
  ) => {
    remove(family);
    const cancel = new AbortController();
    setUploads((current) => ({ ...current, [family]: { ...source, cancel, progress: 0 } }));
    send(cancel)
      .then(({ uploadId, inspection }) => {
        if (cancel.signal.aborted) {
          discardApkUpload(uploadId).catch(() => undefined);
          return;
        }
        patch(family, cancel, { uploadId, inspection, progress: 1 });
        latestOnUploaded.current?.(family, uploadId, inspection, source.fromStorage);
      })
      .catch((error) => {
        if (!cancel.signal.aborted) patch(family, cancel, { error: releaseErrorMessage(error) });
      });
  };

  const start = (family: HardwareFamily, file: File) =>
    track(family, { fileName: file.name, sizeBytes: file.size, fromStorage: false }, (cancel) =>
      uploadApk(file, (progress) => patch(family, cancel, { progress }), cancel.signal),
    );

  const pickStored = (family: HardwareFamily, stored: StoredApk) =>
    track(
      family,
      { fileName: stored.fileName, sizeBytes: stored.sizeBytes, fromStorage: true },
      () => pickStoredApk(stored),
    );

  const markSaved = (families: HardwareFamily[]) => {
    const markFamilies = (current: FamilyUploads) => {
      const next = { ...current };
      for (const family of families) {
        const upload = next[family];
        if (upload) next[family] = { ...upload, saved: true };
      }
      return next;
    };
    // The page may navigate away before the next render, and its cleanup must not discard these.
    uploadsOnPage.current = markFamilies(uploadsOnPage.current);
    setUploads(markFamilies);
  };

  const fail = (family: HardwareFamily, error: string) => {
    const upload = uploadsOnPage.current[family];
    if (!upload) return;
    abandonUpload(upload);
    patch(family, upload.cancel, { error, uploadId: undefined, inspection: undefined });
  };

  return { uploads, start, pickStored, remove, markSaved, fail };
}
